import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { usePortal } from '../../context/PortalContext';
import { API_URL } from '../../services/api';
import DialCodePhoneInput from '../Patients/DialCodePhoneInput';
import { applyDialCodeToPhone, getDialCodeForCountry } from '../../utils/phoneFormatter';
import { getDeviceId } from '../../utils/deviceFingerprint';

const RESEND_COOLDOWN_SECONDS = 60;

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const facilityTypes = [
  'Clinic',
  'Medical Centre',
  'Health Centre',
  'Hospital',
  'Referral Hospital',
  'Specialized Hospital',
  'Maternity Home',
  'Nursing Home',
  'Drug shop',
  'Diagnostic Centre',
];

// Facility types whose demos are provisioned on the entry-tier plans.
// Keep in sync with _FACILITY_PLAN_MAP in demo_request_service.py.
const ENTRY_TIER_FACILITY_TYPES = ['Drug shop', 'Diagnostic Centre'];
const ENTRY_TIER_PLAN_NAMES = { 'Drug shop': 'Starter', 'Diagnostic Centre': 'Starter 2' };

const countries = [
  'Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Nigeria', 'Ghana', 'South Africa',
  'United Kingdom', 'United States', 'India', 'Other',
];

const facilitySizes = [
  'Outpatient clinic only',
  '1–20 beds',
  '21–50 beds',
  '51–100 beds',
  '101–250 beds',
  'More than 250 beds',
];

const currentSystems = [
  'Paper records',
  'Excel / spreadsheets',
  'Clinicmaster',
  'OpenEMR',
  'Bahmni Standard/Lite',
  'Quickbooks',
  'Another hospital system',
  'No system yet',
];

const referralSources = [
  'Google search',
  'Social media',
  'Colleague or friend',
  'Conference or event',
  'An Ospito+ customer',
  'Other',
];

const getErrorMessage = async (response) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (data.detail?.message) return data.detail.message;
    if (Array.isArray(data.detail)) return data.detail.map((item) => item.msg).join('; ');
  } catch {
    // The fallback below handles non-JSON responses.
  }
  return 'We could not submit your request. Please try again.';
};

const getRetryAfterSeconds = (response, message) => {
  const headerValue = Number(response.headers.get('Retry-After'));
  if (Number.isFinite(headerValue) && headerValue > 0) return Math.ceil(headerValue);
  const match = message.match(/Please wait (\d+) seconds?/i);
  return match ? Number(match[1]) : 0;
};

const initialForm = (portalUser) => ({
  facility_name: '',
  facility_type: '',
  country: '',
  city: '',
  email: portalUser?.email || '',
  whatsapp_number: portalUser?.phone || '',
  admin_full_name: portalUser?.name || '',
  facility_size: '',
  current_system: '',
  referral_source: '',
});

const DemoRequestPanel = ({ isOpen, onClose }) => {
  const { portalUser } = usePortal();
  const [form, setForm] = useState(() => initialForm(portalUser));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const [verifyInfo, setVerifyInfo] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [submitCooldownIn, setSubmitCooldownIn] = useState(0);
  const [cooldownError, setCooldownError] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setForm(initialForm(portalUser));
    setError('');
    setSubmitted(false);
    setTurnstileToken(null);
    setVerifyInfo(null);
    setCode('');
    setVerifying(false);
    setResending(false);
    setResendIn(0);
    setSubmitCooldownIn(0);
    setCooldownError(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen, portalUser]);

  // Load the Turnstile script and render the widget explicitly so the
  // success/expiry callbacks are real functions. The container div is
  // recreated on every open, so the widget id is reset before rendering.
  useEffect(() => {
    if (!isOpen || !TURNSTILE_SITE_KEY || submitted || verifyInfo) return undefined;
    turnstileWidgetId.current = null;
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current !== null) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'auto',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        'error-callback': () => setTurnstileToken(null),
      });
    };
    if (window.turnstile) {
      renderWidget();
      return undefined;
    }
    let script = document.getElementById('turnstile-script');
    if (!script) {
      script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderWidget);
    return () => script.removeEventListener('load', renderWidget);
  }, [isOpen, submitted, verifyInfo]);

  // Countdown timer for the "resend code" cooldown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // The initial-submit cooldown is server-enforced and also shown in the UI.
  useEffect(() => {
    if (submitCooldownIn <= 0) return undefined;
    const timer = setTimeout(() => setSubmitCooldownIn((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [submitCooldownIn]);

  useEffect(() => {
    if (submitCooldownIn === 0 && cooldownError) {
      setCooldownError(false);
      setError('');
    }
  }, [submitCooldownIn, cooldownError]);

  // When country changes, re-map any typed WhatsApp number to the new dial code.
  // An empty field stays empty — the dial code only appears once the user types.
  useEffect(() => {
    const dialCode = getDialCodeForCountry(form.country);
    if (!dialCode) return;
    setForm((current) => {
      const existing = current.whatsapp_number || '';
      if (!existing.replace(/[^\d]/g, '')) return current;
      return { ...current, whatsapp_number: applyDialCodeToPhone(existing, form.country) };
    });
  }, [form.country]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      // Bed-count doesn't apply to drug shops / diagnostic centres — lock the
      // size field to "Not Applicable" while an entry-tier type is selected,
      // and clear it again if the user switches back to a clinical facility.
      if (name === 'facility_type') {
        if (ENTRY_TIER_FACILITY_TYPES.includes(value)) {
          next.facility_size = 'Not Applicable';
        } else if (current.facility_size === 'Not Applicable') {
          next.facility_size = '';
        }
      }
      return next;
    });
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitCooldownIn > 0) return;
    setSubmitting(true);
    setError('');
    setCooldownError(false);
    try {
      const response = await fetch(`${API_URL}/api/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, device_id: getDeviceId(), turnstile_token: turnstileToken }),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response);
        const retryAfter = getRetryAfterSeconds(response, message);
        if (retryAfter > 0) {
          setSubmitCooldownIn(retryAfter);
          setCooldownError(true);
        }
        throw new Error(message);
      }
      setSubmitCooldownIn(0);
      const data = await response.json().catch(() => ({}));
      if (data.verification_required) {
        setVerifyInfo({ request_id: data.request_id, email: data.email });
        setResendIn(RESEND_COOLDOWN_SECONDS);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.message || 'We could not submit your request. Please try again.');
      // Turnstile tokens are single-use — reset so the user can retry.
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.reset(turnstileWidgetId.current);
        setTurnstileToken(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (!verifyInfo) return;
    setVerifying(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/demo-requests/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: verifyInfo.request_id, code }),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response));
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!verifyInfo || resendIn > 0) return;
    setResending(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/demo-requests/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: verifyInfo.request_id }),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response));
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setCode('');
    } catch (err) {
      setError(err.message || 'Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const isEntryTierType = ENTRY_TIER_FACILITY_TYPES.includes(form.facility_type);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" aria-labelledby="demo-request-title">
      <button
        type="button"
        aria-label="Close demo request panel"
        className="absolute inset-0 w-full cursor-default bg-slate-950/45 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="animate-slide-in-right absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl dark:bg-gray-900">
        {/* Header — ModuleGuard-style: icon tile + compact title + muted sub */}
        <div className="relative shrink-0 border-b border-gray-100 px-6 py-5 dark:border-gray-800 sm:px-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="flex flex-col items-center gap-2 px-8 text-center">
            <div className="flex items-baseline">
              <span className="text-3xl font-bold leading-none bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>Ospito</span>
              <span className="text-xl font-bold leading-none bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1 inline-block" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>+</span>
            </div>
            <div>
              <h2 id="demo-request-title" className="text-base font-bold text-gray-900 dark:text-white">Request a demo</h2>
              <p className="mt-0.5 max-w-sm text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                Tell us about your facility — we review every request and email a secure activation link once approved.
              </p>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-900/30">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="mt-3 text-base font-bold text-gray-900 dark:text-white">Request received</h3>
            <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
              Thank you. We will review your details and send an activation link to your email when your demo is approved.
            </p>
            <div className="mt-5 w-full max-w-sm space-y-3 rounded-xl border border-gray-200/80 bg-gray-50 p-4 text-left dark:border-gray-800 dark:bg-gray-800/50">
              <div className="flex items-start gap-2.5">
                <EnvelopeIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />
                <p className="text-xs text-gray-600 dark:text-gray-300">Watch your email for the approval and password-setup link.</p>
              </div>
              <div className="flex items-start gap-2.5">
                <ChatBubbleLeftRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />
                <p className="text-xs text-gray-600 dark:text-gray-300">We may also contact you on WhatsApp to arrange the walkthrough.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700"
            >
              Done
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : verifyInfo ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center sm:px-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-900/30">
              <EnvelopeIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <h3 className="mt-3 text-base font-bold text-gray-900 dark:text-white">Check your email</h3>
            <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
              We sent a 6-digit verification code to <span className="font-semibold text-gray-900 dark:text-white">{verifyInfo.email}</span>. Enter it below to submit your request.
            </p>
            {error && (
              <div className="mt-5 w-full max-w-xs rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}
            <form onSubmit={handleVerifyCode} className="mt-7 w-full max-w-xs">
              <input
                id="verify-code"
                name="code"
                value={code}
                onChange={(event) => { setCode(event.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
                maxLength={6}
                required
                autoFocus
                className="input-rx text-center text-2xl font-bold tracking-[0.5em] placeholder:text-base placeholder:tracking-normal placeholder:text-gray-300 dark:placeholder:text-gray-600"
              />
              <button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {verifying ? 'Verifying…' : 'Verify & submit request'}
                {!verifying && <ArrowRightIcon className="h-3.5 w-3.5" />}
              </button>
            </form>
            <div className="mt-5 flex items-center justify-center gap-4 text-xs">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending || resendIn > 0}
                className="font-semibold text-teal-600 transition-colors hover:text-teal-700 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-teal-400 dark:hover:text-teal-300 dark:disabled:text-gray-500"
              >
                {resending ? 'Sending…' : resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
              </button>
              <span className="h-3 w-px bg-gray-200 dark:bg-gray-700" />
              <button
                type="button"
                onClick={() => { setVerifyInfo(null); setCode(''); setError(''); }}
                className="font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Use a different email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
              {error && (
                <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
                  {cooldownError && submitCooldownIn > 0
                    ? `Please wait ${submitCooldownIn} seconds before submitting another request.`
                    : error}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">Facility details</h3>
                  <div className="mt-3 space-y-4">
                    <div>
                      <label htmlFor="facility_name" className="label-rx label-rx-required text-xs">Facility / organisation name</label>
                      <input id="facility_name" name="facility_name" value={form.facility_name} onChange={handleChange} required maxLength={255} className="input-rx text-base" placeholder="City Medical Centre" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="facility_type" className="label-rx label-rx-required text-xs">Facility type</label>
                        <select id="facility_type" name="facility_type" value={form.facility_type} onChange={handleChange} required className="input-rx text-base">
                          <option value="">Select type</option>
                          {facilityTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="country" className="label-rx label-rx-required text-xs">Country</label>
                        <input id="country" name="country" value={form.country} onChange={handleChange} required maxLength={100} list="demo-country-list" className="input-rx text-base" placeholder="Start typing…" />
                        <datalist id="demo-country-list">{countries.map((country) => <option key={country} value={country} />)}</datalist>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="city" className="label-rx label-rx-required text-xs">City</label>
                        <input id="city" name="city" value={form.city} onChange={handleChange} required maxLength={100} className="input-rx text-base" placeholder="Kampala" />
                      </div>
                      <div>
                        <label htmlFor="facility_size" className="label-rx label-rx-required text-xs">Facility size</label>
                        <select id="facility_size" name="facility_size" value={form.facility_size} onChange={handleChange} required disabled={isEntryTierType} className={`input-rx text-base${isEntryTierType ? ' cursor-not-allowed opacity-60' : ''}`}>
                          <option value="">Select size</option>
                          {isEntryTierType && <option value="Not Applicable">Not Applicable</option>}
                          {facilitySizes.map((size) => <option key={size} value={size}>{size}</option>)}
                        </select>
                        {isEntryTierType && (
                          <p className="mt-1 text-[11px] font-medium text-teal-600 dark:text-teal-400">
                            Not applicable — your demo will be configured for the {ENTRY_TIER_PLAN_NAMES[form.facility_type]} plan.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5 dark:border-gray-700">
                  <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">Administrator contact</h3>
                  <div className="mt-3 space-y-4">
                    <div>
                      <label htmlFor="admin_full_name" className="label-rx label-rx-required text-xs">Admin full name</label>
                      <input id="admin_full_name" name="admin_full_name" value={form.admin_full_name} onChange={handleChange} required maxLength={255} autoComplete="name" className="input-rx text-base" placeholder="Your full name" />
                    </div>
                    <div>
                      <label htmlFor="email" className="label-rx label-rx-required text-xs">Email address</label>
                      <input id="email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={255} autoComplete="email" className="input-rx text-base" placeholder="you@facility.org" />
                    </div>
                    <div>
                      <label htmlFor="whatsapp_number" className="label-rx label-rx-required text-xs">WhatsApp number</label>
                      <DialCodePhoneInput
                        id="whatsapp_number"
                        name="whatsapp_number"
                        value={form.whatsapp_number}
                        country={form.country}
                        required
                        autoComplete="tel"
                        placeholder="700 000 000"
                        inputClassName="text-base"
                        onChange={(value) => {
                          setForm((current) => ({ ...current, whatsapp_number: value }));
                          setError('');
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5 dark:border-gray-700">
                  <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">About your setup</h3>
                  <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="current_system" className="label-rx text-xs">Current system <span className="font-normal text-gray-400">(optional)</span></label>
                      <input id="current_system" name="current_system" value={form.current_system} onChange={handleChange} maxLength={255} list="demo-system-list" className="input-rx text-base" placeholder="Paper, Excel, another HMS…" />
                      <datalist id="demo-system-list">{currentSystems.map((system) => <option key={system} value={system} />)}</datalist>
                    </div>
                    <div>
                      <label htmlFor="referral_source" className="label-rx label-rx-required text-xs">How did you hear about us?</label>
                      <input id="referral_source" name="referral_source" value={form.referral_source} onChange={handleChange} required maxLength={150} list="demo-referral-list" className="input-rx text-base" placeholder="Start typing…" />
                      <datalist id="demo-referral-list">{referralSources.map((source) => <option key={source} value={source} />)}</datalist>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t border-gray-100 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800/70 sm:px-8">
              {TURNSTILE_SITE_KEY && (
                <div className="mb-3 flex justify-center">
                  <div ref={turnstileRef} />
                </div>
              )}
              <button type="submit" disabled={submitting || submitCooldownIn > 0 || (TURNSTILE_SITE_KEY && !turnstileToken)} className="mx-auto flex w-56 items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60">
                {submitting
                  ? 'Submitting request…'
                  : submitCooldownIn > 0
                    ? `Try again in ${submitCooldownIn}s`
                    : 'Submit demo request'}
                {!submitting && submitCooldownIn <= 0 && <ArrowRightIcon className="h-4 w-4" />}
              </button>
              <p className="mt-2 text-center text-[11px] text-gray-400 dark:text-gray-500">No password or payment details are required</p>
              <p className="mt-1 text-center text-[11px] text-gray-400 dark:text-gray-500">
                Need help?{' '}
                <a href="mailto:ospitoplus@gmail.com" className="font-medium text-teal-600 hover:underline dark:text-teal-400">
                  ospitoplus@gmail.com
                </a>
              </p>
            </div>
          </form>
        )}
      </aside>
    </div>,
    document.body,
  );
};

export default DemoRequestPanel;
